import * as React from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import {ReactComponent as CircleCheck} from '../../assets/circle-check-fill-green.svg';
import { CheckCircleOutline } from '@mui/icons-material';

export interface PlanProps {
  action: React.ReactNode;
  currency: string;
  description?: string;
  features: string[];
  id: string;
  name: string;
  popular?: boolean;
  price: number;
}

export function Plan({ action, currency, description, id, features, name, price }: PlanProps): React.JSX.Element {
  return (
    <Card sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Stack spacing={2} sx={{ p: 3 }}>
        <Box sx={{ height: '30px', width: '40px' }}>
          <PlanIcon name={id} />
        </Box>
        <Typography variant="h6" sx={{color: '#1D89D2', fontWeight: 700}}>{name}</Typography>
        <Box sx={{ alignItems: 'flex-end', display: 'flex', gap: 1 }}>
          <Typography variant="h4">
            {new Intl.NumberFormat('en-US', {
              style: 'currency',
              currency,
              maximumFractionDigits: price === 0 ? 0 : 2,
            }).format(price)}
          </Typography>
          <Typography color="text.secondary" variant="subtitle2">
            /mo
          </Typography>
        </Box>
        <Typography color="text.secondary" variant="body2">
          <span className="content-text" dangerouslySetInnerHTML={{__html: description?description:''}}
                                  ></span>
        </Typography>
      </Stack>
      <Divider />
      <Stack spacing={6} sx={{ display: 'flex', flex: '1 1 auto', p: 3 }}>
        <Stack spacing={2} sx={{ flex: '1 1 auto' }}>
          {features.map((feature) => (
            <Stack direction="row" key={feature} spacing={1} sx={{ alignItems: 'top', display: 'flex' }}>
              <Box sx={{ display: 'flex', marginTop: '0.1rem'}}>
                <CheckCircleOutline sx={{fill:'#1D89D2' }} fontSize="small" />
              </Box>
              <Typography variant="subtitle2">{feature}</Typography>
            </Stack>
          ))}
        </Stack>
        {action}
      </Stack>
    </Card>
  );
}

interface PlanIconProps {
  name: string;
}

function PlanIcon({ name }: PlanIconProps): React.JSX.Element | null {
  switch (name) {
    case 'Standard':
      return (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="33" viewBox="0 0 24 33" fill="none">
          <path d="M13.7896 0.492981L23.019 5.55771C23.2322 5.67468 23.4096 5.84498 23.5331 6.05102C23.6566 6.25713 23.7217 6.49154 23.7217 6.73031C23.7217 6.96907 23.6566 7.20348 23.5331 7.4096C23.4096 7.61564 23.2322 7.78594 23.019 7.9029L13.7897 12.9679C13.2007 13.2911 12.5364 13.4609 11.8608 13.4609C11.1851 13.4609 10.5208 13.2911 9.93187 12.9679L0.70253 7.9029C0.48938 7.78594 0.311932 7.61564 0.188435 7.4096C0.0649395 7.20348 -0.000162125 6.96907 -0.000162125 6.73031C-0.000162125 6.49154 0.0649395 6.25713 0.188435 6.05102C0.311932 5.84498 0.48938 5.67468 0.70253 5.55771L9.93187 0.492981C10.5208 0.169739 11.1851 -5.72205e-06 11.8608 -5.72205e-06C12.5364 -5.72205e-06 13.2007 0.169739 13.7896 0.492981Z" fill="#1D89D2"/>
        </svg>
      );
    case 'Premium':
      return (
        <svg xmlns="http://www.w3.org/2000/svg" width="32" height="33" viewBox="0 0 32 33" fill="none">
          <g clip-path="url(#clip0_333_5885)">
          <path d="M17.8282 0.492981L27.0576 5.55771C27.2708 5.67468 27.4482 5.84498 27.5717 6.05102C27.6952 6.25713 27.7603 6.49154 27.7603 6.73031C27.7603 6.96907 27.6952 7.20348 27.5717 7.4096C27.4482 7.61564 27.2708 7.78594 27.0576 7.9029L17.8283 12.9679C17.2393 13.2911 16.575 13.4609 15.8994 13.4609C15.2237 13.4609 14.5594 13.2911 13.9704 12.9679L4.7411 7.9029C4.52795 7.78594 4.35051 7.61564 4.22701 7.4096C4.10351 7.20348 4.03841 6.96907 4.03841 6.73031C4.03841 6.49154 4.10351 6.25713 4.22701 6.05102C4.35051 5.84498 4.52795 5.67468 4.7411 5.55771L13.9704 0.492981C14.5594 0.169739 15.2237 -5.72205e-06 15.8994 -5.72205e-06C16.575 -5.72205e-06 17.2393 0.169739 17.8282 0.492981Z" fill="#1D89D2"/>
          <path opacity="0.7" d="M17.7782 5.24762L30.996 12.5011C31.2092 12.6181 31.3866 12.7884 31.5101 12.9944C31.6336 13.2005 31.6987 13.4349 31.6987 13.6737C31.6987 13.9125 31.6336 14.147 31.5101 14.353C31.3866 14.5591 31.2092 14.7293 30.996 14.8464L17.7782 22.0998C17.1893 22.4229 16.525 22.5928 15.8494 22.5928C15.1738 22.5928 14.5095 22.4229 13.9205 22.0998L0.702734 14.8464C0.489584 14.7293 0.312134 14.5591 0.188637 14.353C0.0651398 14.147 4.19617e-05 13.9125 4.19617e-05 13.6737C4.19617e-05 13.4349 0.0651398 13.2005 0.188637 12.9944C0.312134 12.7884 0.489584 12.6181 0.702734 12.5011L13.9205 5.24762C14.5095 4.92445 15.1738 4.75471 15.8494 4.75471C16.525 4.75471 17.1893 4.92445 17.7782 5.24762Z" fill="#529AF9"/>
          </g>
          <defs>
          <clipPath id="clip0_333_5885">
          <rect width="32" height="33" fill="white"/>
          </clipPath>
          </defs>
        </svg>
      );
    case 'Professional':
      return (
        <svg xmlns="http://www.w3.org/2000/svg" width="42" height="33" viewBox="0 0 42 33" fill="none">
          <path d="M22.6744 0.492981L31.9038 5.55771C32.117 5.67468 32.2944 5.84498 32.4178 6.05102C32.5413 6.25713 32.6064 6.49154 32.6064 6.73031C32.6064 6.96907 32.5413 7.20348 32.4178 7.4096C32.2944 7.61564 32.117 7.78594 31.9038 7.9029L22.6745 12.9679C22.0854 13.2911 21.4212 13.4609 20.7456 13.4609C20.0698 13.4609 19.4056 13.2911 18.8166 12.9679L9.5873 7.9029C9.37415 7.78594 9.1967 7.61564 9.0732 7.4096C8.94971 7.20348 8.8846 6.96907 8.8846 6.73031C8.8846 6.49154 8.94971 6.25713 9.0732 6.05102C9.1967 5.84498 9.37415 5.67468 9.5873 5.55771L18.8166 0.492981C19.4056 0.169739 20.0698 -5.72205e-06 20.7456 -5.72205e-06C21.4212 -5.72205e-06 22.0854 0.169739 22.6744 0.492981Z" fill="#0066DE"/>
          <path opacity="0.7" d="M22.6244 5.24762L35.8422 12.5011C36.0554 12.6181 36.2328 12.7884 36.3563 12.9944C36.4798 13.2005 36.5449 13.4349 36.5449 13.6737C36.5449 13.9125 36.4798 14.147 36.3563 14.353C36.2328 14.5591 36.0554 14.7293 35.8422 14.8464L22.6244 22.0998C22.0355 22.4229 21.3712 22.5928 20.6956 22.5928C20.0199 22.5928 19.3557 22.4229 18.7667 22.0998L5.54893 14.8464C5.33578 14.7293 5.15833 14.5591 5.03483 14.353C4.91133 14.147 4.84623 13.9125 4.84623 13.6737C4.84623 13.4349 4.91133 13.2005 5.03483 12.9944C5.15833 12.7884 5.33578 12.6181 5.54893 12.5011L18.7667 5.24762C19.3557 4.92445 20.0199 4.75471 20.6956 4.75471C21.3712 4.75471 22.0355 4.92445 22.6244 5.24762Z" fill="#1D89D2"/>
          <path opacity="0.4" d="M22.9257 10.0016L41.2984 20.0841C41.5113 20.201 41.6884 20.3709 41.8117 20.5767C41.935 20.7824 42 21.0165 42 21.2549C42 21.4933 41.935 21.7273 41.8117 21.9331C41.6884 22.1388 41.5113 22.3088 41.2984 22.4256L22.9257 32.5078C22.3377 32.8305 21.6745 33 21 33C20.3254 33 19.6623 32.8305 19.0743 32.5078L0.701542 22.4256C0.488743 22.3088 0.311581 22.1388 0.188286 21.9331C0.0649948 21.7273 0 21.4933 0 21.2549C0 21.0165 0.0649948 20.7824 0.188286 20.5767C0.311581 20.3709 0.488743 20.201 0.701542 20.0841L19.0743 10.0016C19.6623 9.67899 20.3254 9.50945 21 9.50945C21.6745 9.50945 22.3377 9.67899 22.9257 10.0016Z" fill="#529AF9"/>
        </svg>
      );
    default:
      return null;
  }
}
